import { animate, style, transition, trigger } from '@angular/animations';
import { CommonModule } from '@angular/common';
import {
  Component,
  DestroyRef,
  OnInit,
  ViewChild,
  inject,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatNativeDateModule } from '@angular/material/core';
import {
  MatCalendar,
  MatCalendarCellCssClasses,
  MatDatepickerModule,
} from '@angular/material/datepicker';
import {
  MAT_DIALOG_DATA,
  MatDialog,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatIconModule } from '@angular/material/icon';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import { Observable, forkJoin, of } from 'rxjs';
import { catchError, finalize, map, tap } from 'rxjs/operators';
import { HolidayDto } from '../../../models/holiday.models';
import { NotesWithMentionsDto } from '../../../models/notes.models';
import {
  TimeOffActionNotesDto,
  TimeOffOverlapDto,
  UpdateTimeOffRequestRequest,
} from '../../../models/time-off.models';
import { NotesService } from '../../../services/notes.service';
import { TimeOffService } from '../../../services/time-off.service';
import {
  ConfirmationDialogComponent,
  ConfirmationDialogData,
} from '../confirmation-dialog/confirmation-dialog.component';
import { TimeOffCarryOverSummaryComponent } from '../time-off-carry-over-summary/time-off-carry-over-summary.component';
import {
  DayRun,
  countBusinessDays,
  formatShortDateRange,
  groupContiguousDays,
  holidayDateKeys,
  parseIsoDateKey,
  toIsoDateKey,
} from '../../../utils/time-off-days.utils';
import { describeApiError } from '../../../utils/api-error.utils';
import {
  OverlapRow,
  overlapConfirmationSection,
  toOverlapRows,
} from '../../../utils/time-off-overlap.utils';

/** What the overlap check found for the days an edit adds to an approved request. */
interface AddedDaysOverlapCheck {
  /** Overlapping requests, each cut down to the days the edit adds. */
  overlaps: TimeOffOverlapDto[];
  /** The check could not run, so the preview says the added days went unchecked. */
  failed: boolean;
}

export interface TimeOffDetailsDialogData {
  timeOffRequestId: number;
  /**
   * Whose request this is. Drives the overlap check an approver sees on a pending request; the
   * check never runs without it, so a caller that leaves it out just gets no warning.
   */
  employeeId?: string;
  employeeName: string;
  statusCode: string;
  /** Holds Resources.TimeOff.Approval: drives the Approve / Return / Cancel Time Off actions. */
  canApprove: boolean;
  /**
   * Holds Resources.TimeOff.Write, the permission the day-adjustment API checks. Unlocks editing
   * of approved requests and of the user's own pending one; without it only the user's own
   * pending request is editable (see canEdit).
   */
  canEditDays?: boolean;
  apiMode: 'admin' | 'self-service';
  /**
   * True when the request belongs to the signed-in user. Lets anyone edit their own pending
   * request, whatever their permissions (see canEdit).
   */
  isOwnRequest?: boolean;
  hasNotes?: boolean;
  hasManagerNotes?: boolean;
  typeName?: string;
  startDate?: string | null;
  endDate?: string | null;
  daysOff?: number;
  /** Calendar days the request covers (yyyy-MM-dd), as the API stores them. Drives the picker. */
  days?: string[];
  /**
   * Subset of `days` that drew from vacation carry-over. Drives the carry-over summary, the same
   * blue box the calendar's event dialog shows (PBI 6682 AC 4).
   */
  carryOverDays?: string[];
  /**
   * Days held by the employee's other pending/approved requests. Never offered when adding, and
   * adding is confined to the gap between the nearest such request on either side.
   */
  otherRequestDays?: string[];
  /**
   * Holidays, if the caller already has them; the dialog fetches them otherwise. The holiday
   * feed is an external call, so passing them in keeps the picker from waiting on it.
   */
  holidays?: HolidayDto[];
}

export interface TimeOffDetailsDialogResult {
  action: 'approve' | 'return' | 'cancel' | 'edit';
  note?: string;
  /**
   * Set when action is 'edit': the full day set the request now covers. The dialog saves the
   * days itself and only closes once the API has accepted them, so callers refresh, not save.
   */
  edit?: UpdateTimeOffRequestRequest;
}

type CalendarView = 'month' | 'year' | 'multi-year';

/** How far, in days, a request may grow in each direction when no other request is nearer. */
export const MAX_EXTENSION_DAYS = 90;
/** The most dates the API accepts in one day-set update (UpdateTimeOffRequestDaysRequest.MaxDays). */
export const MAX_REQUEST_DAYS = 366;

@Component({
  selector: 'app-time-off-details-dialog',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatDatepickerModule,
    MatNativeDateModule,
    MatIconModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    TimeOffCarryOverSummaryComponent,
  ],
  templateUrl: './time-off-details-dialog.component.html',
  styleUrls: ['./time-off-details-dialog.component.scss'],
  animations: [
    // The edit form (calendar included) unfolds from under the employee row and folds back.
    trigger('slideDown', [
      transition(':enter', [
        style({
          height: 0,
          opacity: 0,
          transform: 'translateY(-8px)',
          overflow: 'hidden',
        }),
        animate(
          '260ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0, transform: 'translateY(-8px)' }),
        ),
      ]),
    ]),
    // The summary row eases back in once editing ends.
    trigger('fadeIn', [
      transition(':enter', [
        style({ opacity: 0, transform: 'translateY(4px)' }),
        animate(
          '200ms 60ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ opacity: 1, transform: 'translateY(0)' }),
        ),
      ]),
    ]),
    // Inline notices grow and shrink in place as days toggle.
    trigger('expandCollapse', [
      transition(':enter', [
        style({ height: 0, opacity: 0, overflow: 'hidden' }),
        animate(
          '220ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: '*', opacity: 1 }),
        ),
      ]),
      transition(':leave', [
        style({ overflow: 'hidden' }),
        animate(
          '180ms cubic-bezier(0.4, 0, 0.2, 1)',
          style({ height: 0, opacity: 0 }),
        ),
      ]),
    ]),
  ],
})
export class TimeOffDetailsDialogComponent implements OnInit {
  private dialogRef =
    inject<
      MatDialogRef<
        TimeOffDetailsDialogComponent,
        TimeOffDetailsDialogResult | null
      >
    >(MatDialogRef);
  data = inject<TimeOffDetailsDialogData>(MAT_DIALOG_DATA);
  private notesService = inject(NotesService);
  private timeOffService = inject(TimeOffService);
  private dialog = inject(MatDialog);
  private destroyRef = inject(DestroyRef);

  requesterNotes: NotesWithMentionsDto | null = null;
  managerNotes: NotesWithMentionsDto | null = null;
  cancellationNotes: NotesWithMentionsDto | null = null;
  /**
   * Status this slot represents (APPROVED or DENIED), used to drive the
   * header badge independently of the request's current status. This lets a
   * CANCELLED request still show its prior APPROVED section above the
   * cancellation section with correct labeling.
   */
  private managerSlotStatus: 'APPROVED' | 'DENIED' | '' = '';
  isLoading = false;

  managerNoteInput = '';
  noteError = false;

  // --- Overlap check: approvers, pending requests, admin hosts only ---
  /**
   * The callout's lines, shaped once when the answer arrives so the template reads plain
   * strings. Empty before the answer, when nothing overlaps, and when the check never ran.
   */
  overlapRows: OverlapRow[] = [];
  isCheckingOverlap = false;
  /**
   * The check failed, so the approver is told the answer is unknown rather than shown nothing,
   * which would read as "no overlap".
   */
  overlapCheckFailed = false;

  // --- Edit mode: Edit Resource Time Off holders on approved requests (plus their own pending
  // one); everyone else on their own pending requests ---
  isEditing = false;
  isLoadingHolidays = false;
  holidaysLoadFailed = false;
  /** True while the day set is being sent to the API; the editor is locked meanwhile. */
  isSaving = false;
  /**
   * True while the days an edit adds to an approved request are checked for overlaps, before the
   * save preview opens; the editor is locked meanwhile, as for a save.
   */
  isCheckingEditOverlap = false;
  /**
   * The last successful overlap answer for an edit, keyed by the added days it was asked about,
   * so going back from the preview and saving again does not repeat the lookup.
   */
  private lastEditOverlapCheck: {
    key: string;
    check: AddedDaysOverlapCheck;
  } | null = null;
  /** The API's reason for refusing the last save, shown in the editor so the days can be fixed. */
  saveError: string | null = null;
  readonly maxExtensionDays = MAX_EXTENSION_DAYS;
  readonly maxRequestDays = MAX_REQUEST_DAYS;
  /** Where the picker opens: the request's first day. */
  calendarStartAt: Date | null = null;
  /** The add window as inclusive dates, so the calendar cannot wander outside it. */
  calendarMinDate: Date | null = null;
  calendarMaxDate: Date | null = null;
  /**
   * The calendar days the request should cover after the edit (yyyy-MM-dd keys). Weekends are
   * ordinary members, as when a request is created; they just never count as days off.
   */
  selectedDays = new Set<string>();
  /**
   * Days the user clicked off during this edit. The gap fill never re-selects them, so a
   * deliberate removal at either end survives a later extension in that direction.
   */
  private deliberatelyRemoved = new Set<string>();
  removedDays: string[] = [];
  addedDays: string[] = [];
  /**
   * How the server is expected to split `selectedDays`: one request per run of consecutive
   * days. The save confirmation previews them; more than one run is called out as a split.
   */
  resultingRequests: DayRun[] = [];
  /**
   * The caller's day list as sent. The API stores every calendar day of the range, holidays
   * included; `originalDays` is this list minus the holidays, which the picker never offers.
   */
  private rawOriginalDays: string[] = [];
  private originalDays = new Set<string>();
  private otherRequestKeys = new Set<string>();
  /**
   * Exclusive bounds for adding days: the nearer of the employee's neighbouring request on that
   * side and MAX_EXTENSION_DAYS beyond the request's own edge. Set for the whole of an edit; null
   * only before editing starts.
   */
  private addWindowStart: string | null = null;
  private addWindowEnd: string | null = null;
  private holidayKeys = new Set<string>();
  private holidaysLoaded = false;
  @ViewChild(MatCalendar) private calendar?: MatCalendar<Date>;

  ngOnInit(): void {
    this.checkOverlap();

    const needsRequester = !!this.data.hasNotes;
    // Always fetch the action-notes endpoint for non-pending requests so we
    // can surface approver/returner/canceller metadata even when no note text exists.
    const statusCode = (this.data.statusCode || '').toUpperCase();
    const isFinalized = statusCode !== '' && statusCode !== 'PENDING';
    const needsManager = !!this.data.hasManagerNotes || isFinalized;

    if (!needsRequester && !needsManager) {
      return;
    }

    this.isLoading = true;

    const requester$ = needsRequester
      ? this.notesService
          .getTimeOffNotes(this.data.timeOffRequestId)
          .pipe(catchError(() => of(null)))
      : of(null);

    const actionNotes$ = needsManager
      ? (this.data.apiMode === 'self-service'
          ? this.timeOffService.getMyActionNotes(this.data.timeOffRequestId)
          : this.timeOffService.getActionNotes(this.data.timeOffRequestId)
        ).pipe(catchError(() => of(null)))
      : of(null);

    forkJoin({ requester: requester$, actionNotes: actionNotes$ }).subscribe({
      next: ({ requester, actionNotes }) => {
        this.requesterNotes = requester;
        this.applyActionNotes(actionNotes);
        this.isLoading = false;
      },
      error: () => {
        this.isLoading = false;
      },
    });
  }

  private applyActionNotes(actionNotes: TimeOffActionNotesDto | null): void {
    if (!actionNotes) {
      this.managerNotes = null;
      this.cancellationNotes = null;
      this.managerSlotStatus = '';
      return;
    }

    // The non-cancel manager block displays either the approval or the return
    // note based on the current request status. CANCELLED requests still show
    // the prior approval (or return) slot above the cancellation section.
    const status = (this.data.statusCode || '').toUpperCase();
    const hasReturn =
      !!actionNotes.return?.notes || !!actionNotes.return?.lastUpdatedBy;
    const hasApproval =
      !!actionNotes.approval?.notes || !!actionNotes.approval?.lastUpdatedBy;

    if (status === 'DENIED' || status === 'RETURNED') {
      this.managerNotes = actionNotes.return;
      this.managerSlotStatus = 'DENIED';
    } else if (status === 'CANCELLED' || status === 'CANCELED') {
      // Prefer whichever prior slot has content; approval is more common.
      if (hasApproval) {
        this.managerNotes = actionNotes.approval;
        this.managerSlotStatus = 'APPROVED';
      } else if (hasReturn) {
        this.managerNotes = actionNotes.return;
        this.managerSlotStatus = 'DENIED';
      } else {
        // No prior approval or return data, but the request was approved
        // before being cancelled — always assign the approved slot so the
        // Approved status badge is still visible in the history.
        this.managerNotes = null;
        this.managerSlotStatus = 'APPROVED';
      }
    } else {
      this.managerNotes = actionNotes.approval;
      this.managerSlotStatus = 'APPROVED';
    }

    this.cancellationNotes = actionNotes.cancellation;
  }

  get hasAnyNotes(): boolean {
    return (
      this.hasManagerHeader ||
      this.hasCancellationHeader ||
      !!this.requesterNotes?.notes?.trim()
    );
  }

  get showActions(): boolean {
    return (
      this.data.canApprove &&
      (this.data.statusCode || '').toUpperCase() === 'PENDING'
    );
  }

  /**
   * Asks the API whether a co-assigned resource in the same practice area is already off on
   * these dates, so the approver knows before deciding. Only an approver looking at a pending
   * request through an admin host is asked: the route needs the approval permission, and the
   * self-service hosts never bind canApprove, so the extra apiMode guard keeps a self-service
   * caller from ever hitting its 403. Separate from the notes forkJoin so a slow matter lookup
   * never holds the notes back.
   */
  private checkOverlap(): void {
    const employeeId = this.data.employeeId;
    // The route binds yyyy-MM-dd only. The Resources page hands the dialog bare keys; the card
    // may hand it a full ISO timestamp, so the key is cut from whatever came in, and a value
    // that will not parse means there is nothing to ask about.
    const startDate = toIsoDateKey(parseIsoDateKey(this.data.startDate));
    const endDate = toIsoDateKey(parseIsoDateKey(this.data.endDate));
    if (
      !this.showActions ||
      this.data.apiMode !== 'admin' ||
      !employeeId ||
      !startDate ||
      !endDate
    ) {
      return;
    }
    this.isCheckingOverlap = true;
    this.timeOffService
      .checkOverlap(employeeId, startDate, endDate)
      // Closing the dialog drops an answer nobody is left to read.
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: (result) => {
          this.overlapRows = toOverlapRows(result);
          this.isCheckingOverlap = false;
        },
        error: (err: unknown) => {
          console.error('Time off overlap check failed:', err);
          this.overlapCheckFailed = true;
          this.isCheckingOverlap = false;
        },
      });
  }

  get showOverlapWarning(): boolean {
    return this.overlapRows.length > 0;
  }

  /**
   * True when some route will accept a cancel: an owner's pending request through the
   * self-service route (no permission needed), or an approved request through the admin route,
   * which takes the approval permission. Nothing else can be cancelled from here, so nothing else
   * is offered a cancel, neither the footer button nor the empty-editor confirmation.
   */
  get canCancelRequest(): boolean {
    if (this.isOwnPendingRequest) return true;
    const code = (this.data.statusCode || '').toUpperCase();
    return this.data.canApprove && code === 'APPROVED';
  }

  /**
   * The owner's own pending request. Mirrors TimeOffService.cancelRequestFor, which withdraws it
   * through the self-service route: no permission needed, and no note taken.
   */
  private get isOwnPendingRequest(): boolean {
    const code = (this.data.statusCode || '').toUpperCase();
    return !!this.data.isOwnRequest && code === 'PENDING';
  }

  /** The manager note as typed, trimmed, or undefined when it is blank. */
  private get optionalManagerNote(): string | undefined {
    return this.managerNoteInput.trim() || undefined;
  }

  /** The footer's Cancel Time Off button. */
  get showCancelAction(): boolean {
    return this.canCancelRequest;
  }

  /** The manager note box goes with approver actions only; a resource withdrawing leaves none. */
  get showManagerNoteInput(): boolean {
    return this.data.canApprove && (this.showActions || this.showCancelAction);
  }

  /**
   * Holders of the Edit Resource Time Off permission edit approved requests (anyone's, their own
   * included) and their own pending request; other people's pending requests go through Approve
   * and Return instead. Everyone else edits only their own pending request. Past requests are not
   * treated differently. Mirrors TimeOffService.UpdateDaysAsync on the API.
   */
  get canEdit(): boolean {
    const status = (this.data.statusCode || '').toUpperCase();
    if (this.data.canEditDays) {
      return (
        status === 'APPROVED' ||
        (status === 'PENDING' && !!this.data.isOwnRequest)
      );
    }
    return !!this.data.isOwnRequest && status === 'PENDING';
  }

  get dateRangeLabel(): string {
    return formatShortDateRange(this.data.startDate, this.data.endDate);
  }

  get daysOffLabel(): string {
    const days = this.data.daysOff;
    if (days === undefined || days === null) return '';
    const rounded = Math.round(days * 100) / 100;
    return `${rounded} ${rounded === 1 ? 'day' : 'days'}`;
  }

  private get isCancelled(): boolean {
    const s = (this.data.statusCode || '').toUpperCase();
    return s === 'CANCELLED' || s === 'CANCELED';
  }

  get hasManagerHeader(): boolean {
    if (this.isCancelled) {
      return !!this.managerSlotStatus;
    }
    return !!this.managerNotes?.lastUpdatedBy || !!this.managerNotes?.notes;
  }

  get hasCancellationHeader(): boolean {
    return (
      this.isCancelled ||
      !!this.cancellationNotes?.lastUpdatedBy ||
      !!this.cancellationNotes?.notes
    );
  }

  get managerNoteHeader(): string {
    const updatedBy = this.managerNotes?.lastUpdatedBy;
    if (!updatedBy) {
      return 'Manager Note';
    }

    const name = this.formatUserName(updatedBy);
    const timestamp = this.formatTimestamp(updatedBy.timestamp);

    const parts: string[] = [];
    if (name) {
      parts.push(`by ${name}`);
    }
    if (timestamp) {
      parts.push(timestamp);
    }

    return parts.length ? parts.join(' - ') : 'Manager Note';
  }

  get cancellationNoteHeader(): string {
    const updatedBy = this.cancellationNotes?.lastUpdatedBy;
    if (!updatedBy) {
      return 'Manager Note';
    }

    const name = this.formatUserName(updatedBy);
    const timestamp = this.formatTimestamp(updatedBy.timestamp);

    const parts: string[] = [];
    if (name) {
      parts.push(`by ${name}`);
    }
    if (timestamp) {
      parts.push(timestamp);
    }

    return parts.length ? parts.join(' - ') : 'Manager Note';
  }

  get managerNoteStatusLabel(): string {
    // For cancelled requests always show the prior-status badge even when
    // no approval note or lastUpdatedBy exists.
    if (this.isCancelled && this.managerSlotStatus) {
      return this.getSlotStatusLabel(this.managerSlotStatus);
    }
    if (!this.managerNotes?.lastUpdatedBy) {
      return '';
    }
    return this.getSlotStatusLabel(this.managerSlotStatus);
  }

  get cancellationNoteStatusLabel(): string {
    // For cancelled requests always show the Cancelled badge.
    if (this.isCancelled || !!this.cancellationNotes?.lastUpdatedBy) {
      return 'Cancelled';
    }
    return '';
  }

  get managerNoteStatusBadgeClass(): string {
    return this.getSlotBadgeClass(this.managerSlotStatus);
  }

  get cancellationNoteStatusBadgeClass(): string {
    return 'badge-cancelled';
  }

  // --- Edit mode ---

  /** Days off in the request as opened: business days only, matching the API's daysOff. */
  get originalDayCount(): number {
    return countBusinessDays(this.originalDays, this.holidayKeys);
  }

  /** Days off the edited request would carry. Weekend days in the set do not add to it. */
  get selectedDayCount(): number {
    return countBusinessDays(this.selectedDays, this.holidayKeys);
  }

  /** Every day removed: saving then offers to cancel the request instead. */
  get noDaysSelected(): boolean {
    return this.selectedDays.size === 0;
  }

  /**
   * Every day removed by someone no route lets cancel: a Write holder without the approval
   * permission on their own approved request. Save is held until a day comes back.
   */
  get cancelBlocked(): boolean {
    return this.noDaysSelected && !this.canCancelRequest;
  }

  /** True once the day set differs from the request as it was opened. */
  get daysChanged(): boolean {
    return this.removedDays.length > 0 || this.addedDays.length > 0;
  }

  /** True when the selection holds more dates than the API accepts in one update. */
  get overDayLimit(): boolean {
    return this.selectedDays.size > MAX_REQUEST_DAYS;
  }

  /**
   * Holidays gate Save because they decide how the days group. An empty day set still saves:
   * saveEdit turns it into an offer to cancel the request.
   */
  get canSaveEdit(): boolean {
    return (
      this.daysChanged &&
      !this.isLoadingHolidays &&
      !this.overDayLimit &&
      !this.cancelBlocked &&
      !this.isEditBusy
    );
  }

  /**
   * The editor is locked while the edit is being checked for overlaps or saved: a change then
   * would alter the day set underneath the check or the request in flight.
   */
  get isEditBusy(): boolean {
    return this.isSaving || this.isCheckingEditOverlap;
  }

  /** Days off, before and after: weekend days in the set never move this figure. */
  get dayCountLabel(): string {
    const count = this.selectedDayCount;
    const original = this.originalDayCount;
    const label = `${count} ${count === 1 ? 'day' : 'days'}`;
    return count === original ? label : `${label} (was ${original})`;
  }

  /**
   * Calendar date filter. The request's own days always stay clickable so they can be removed
   * or restored. Anything else, weekends included, must be free of holidays and of the
   * employee's other requests and must lie inside the add window.
   */
  readonly isSelectableDay = (date: Date | null): boolean => {
    // Every cell greys out while the day set is being checked or is on its way to the API (see
    // checkAddedDaysForOverlap and submitEdit).
    if (!date || this.isEditBusy) return false;
    const key = toIsoDateKey(date);
    if (this.originalDays.has(key)) return true;
    return (
      this.isWithinAddWindow(key) &&
      !this.holidayKeys.has(key) &&
      !this.otherRequestKeys.has(key)
    );
  };

  /** Calendar cell classes; the legend under the picker mirrors these states. */
  readonly dayClass = (
    date: Date,
    view: CalendarView,
  ): MatCalendarCellCssClasses => {
    if (view !== 'month') return '';
    const key = toIsoDateKey(date);
    const classes: string[] = [];
    if (this.originalDays.has(key)) {
      classes.push(this.selectedDays.has(key) ? 'day-kept' : 'day-removed');
    } else if (this.selectedDays.has(key)) {
      classes.push('day-added');
    } else if (this.otherRequestKeys.has(key)) {
      classes.push('day-other-request');
    } else if (this.holidayKeys.has(key)) {
      classes.push('day-holiday');
    }
    return classes;
  };

  onDayToggled(date: Date | null): void {
    // Locked mid-save: a toggle now would change the set underneath the request in flight, so a
    // failure could describe days no longer on screen and a success could save the wrong set.
    if (this.isEditBusy || !date || !this.isSelectableDay(date)) return;
    // The API's last refusal and the last overlap answer described the old day set; any change
    // retires both.
    this.saveError = null;
    this.lastEditOverlapCheck = null;
    const key = toIsoDateKey(date);
    if (this.selectedDays.has(key)) {
      this.selectedDays.delete(key);
      this.deliberatelyRemoved.add(key);
    } else {
      this.deliberatelyRemoved.delete(key);
      this.selectedDays.add(key);
      this.fillGapTo(key);
    }
    this.recomputeEditState();
    // The calendar only re-evaluates dateClass on a re-render; this forces one.
    this.calendar?.updateTodaysDate();
  }

  /**
   * Picking a day beyond either end of the selection also selects every selectable day between
   * it and the selection, so extending a request never leaves an accidental gap. Holidays are
   * skipped (they bridge anyway), as is any day the user clicked off earlier in this edit: the
   * fill only covers days that were never chosen, so it cannot quietly undo a removal. Each
   * filled day can still be clicked off on purpose. A day picked inside the selection's span
   * fills nothing: gaps in there are deliberate removals too.
   */
  private fillGapTo(key: string): void {
    const others = [...this.selectedDays].filter((k) => k !== key).sort();
    if (!others.length) return;
    const first = others[0];
    const last = others[others.length - 1];
    let from: string;
    let to: string;
    if (key < first) {
      from = key;
      to = first;
    } else if (key > last) {
      from = last;
      to = key;
    } else {
      return;
    }
    const cursor = parseIsoDateKey(from);
    if (!cursor) return;
    for (
      cursor.setDate(cursor.getDate() + 1);
      toIsoDateKey(cursor) < to;
      cursor.setDate(cursor.getDate() + 1)
    ) {
      const cursorKey = toIsoDateKey(cursor);
      if (
        this.isSelectableDay(cursor) &&
        !this.deliberatelyRemoved.has(cursorKey)
      ) {
        this.selectedDays.add(cursorKey);
      }
    }
  }

  startEdit(): void {
    if (!this.canEdit) return;
    this.seedHolidaysFromCaller();
    this.rawOriginalDays = this.resolveOriginalDays();
    this.rebuildOriginalDays();
    this.selectedDays = new Set(this.originalDays);
    this.deliberatelyRemoved = new Set();
    this.otherRequestKeys = new Set(
      (this.data.otherRequestDays ?? [])
        .map(this.normalizeDayKey)
        .filter((key) => !this.originalDays.has(key)),
    );
    this.rebuildAddWindow();
    const firstDay = [...this.originalDays].sort()[0];
    this.calendarStartAt = parseIsoDateKey(firstDay ?? this.data.startDate);
    this.saveError = null;
    this.lastEditOverlapCheck = null;
    this.isEditing = true;
    this.recomputeEditState();
    this.ensureHolidays();
  }

  cancelEdit(): void {
    if (this.isEditBusy) return;
    this.saveError = null;
    this.isEditing = false;
    this.selectedDays = new Set();
    this.deliberatelyRemoved = new Set();
    this.recomputeEditState();
  }

  saveEdit(): void {
    if (!this.canSaveEdit) return;
    if (this.noDaysSelected) {
      this.confirmCancelRequest();
      return;
    }
    this.confirmSave({
      action: 'edit',
      edit: { days: this.buildPayloadDays() },
    });
  }

  /**
   * The day set the API expects: every calendar day of every resulting request, as the server
   * stores them. The picker never shows holidays, so each run is expanded from its first to its
   * last day, which puts back the holidays inside it; a holiday between two runs stays out, since
   * the server splits on calendar adjacency and the preview already shows that split. A holiday
   * the stored request began or ended on sits outside the picker altogether; it is kept while the
   * request still starts or ends on the same visible day, so its stored span does not shift.
   */
  private buildPayloadDays(): string[] {
    const days = new Set<string>();
    const runs = this.resultingRequests;
    for (const run of runs) {
      for (
        let key = run.start;
        key <= run.end;
        key = this.shiftDayKey(key, 1)
      ) {
        days.add(key);
      }
    }
    const visible = [...this.originalDays].sort();
    const firstVisible = visible[0];
    const lastVisible = visible[visible.length - 1];
    if (firstVisible && runs[0]?.start === firstVisible) {
      this.rawOriginalDays
        .filter((key) => key < firstVisible && this.holidayKeys.has(key))
        .forEach((key) => days.add(key));
    }
    if (lastVisible && runs[runs.length - 1]?.end === lastVisible) {
      this.rawOriginalDays
        .filter((key) => key > lastVisible && this.holidayKeys.has(key))
        .forEach((key) => days.add(key));
    }
    return [...days].sort();
  }

  /**
   * Every save is previewed first: what was removed or added, and the dates the request will
   * cover afterwards. A split, where the remaining days are no longer consecutive, is called out
   * because it changes what the requester and the calendar will see. Days added to an approved
   * request are checked for overlaps before the preview opens, and any are listed in it.
   */
  private confirmSave(result: TimeOffDetailsDialogResult): void {
    this.checkAddedDaysForOverlap()
      // A preview must never open after the dialog it belongs to has gone.
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((check) => this.openSavePreview(result, check));
  }

  /**
   * Days added to an approved request are approved the moment they save, so the approver gets
   * the warning the add row gives before an approver's own save. Only the added days are asked
   * about: removing days cannot create an overlap, and the days already approved were decided
   * before. A pending request is left alone, because whoever approves it sees the warning then.
   * The route needs the approval permission, so only an approver through an admin host asks. A
   * failed check still lets the preview open, which then says the added days went unchecked.
   */
  private checkAddedDaysForOverlap(): Observable<AddedDaysOverlapCheck> {
    const employeeId = this.data.employeeId;
    const added = this.addedDays;
    const isApproved =
      (this.data.statusCode || '').toUpperCase() === 'APPROVED';
    if (
      !isApproved ||
      !this.data.canApprove ||
      this.data.apiMode !== 'admin' ||
      !employeeId ||
      added.length === 0
    ) {
      return of({ overlaps: [], failed: false });
    }

    // Save, Go Back, Save asks about the same days again; the last answer still holds while they
    // are unchanged, and the lookup behind it is slow. Any toggle clears it (see onDayToggled).
    const cacheKey = added.join(',');
    if (this.lastEditOverlapCheck?.key === cacheKey) {
      return of(this.lastEditOverlapCheck.check);
    }

    const addedKeys = new Set(added);
    const couldClose = this.dialogRef.disableClose;
    this.isCheckingEditOverlap = true;
    // A backdrop click mid-check would leave the preview opening over a closed dialog.
    this.dialogRef.disableClose = true;
    // The date filter now refuses every cell; the calendar only re-reads it on a repaint.
    this.calendar?.updateTodaysDate();
    return this.timeOffService
      .checkOverlap(employeeId, added[0], added[added.length - 1])
      .pipe(
        // The range runs from the first added day to the last, so it can span days that were not
        // added (a gap between stretches added on either side); each overlap keeps only the days
        // the edit actually adds.
        map((result) => ({
          overlaps: (result.hasOverlap ? result.overlaps : [])
            .map((overlap) => ({
              ...overlap,
              days: overlap.days.filter((day) => addedKeys.has(day)),
            }))
            .filter((overlap) => overlap.days.length > 0),
          failed: false,
        })),
        // Only a real answer is kept: a failure is asked again on the next Save.
        tap((check) => (this.lastEditOverlapCheck = { key: cacheKey, check })),
        catchError((err: unknown) => {
          console.error('Time off overlap check failed:', err);
          return of({ overlaps: [], failed: true });
        }),
        finalize(() => {
          this.isCheckingEditOverlap = false;
          this.dialogRef.disableClose = couldClose;
          this.calendar?.updateTodaysDate();
        }),
      );
  }

  private openSavePreview(
    result: TimeOffDetailsDialogResult,
    check: AddedDaysOverlapCheck,
  ): void {
    const runs = this.resultingRequests;
    const count = runs.length;
    const isSplit = count > 1;
    const hasOverlap = check.overlaps.length > 0;
    const message = [
      isSplit
        ? `The remaining days are no longer consecutive, so saving replaces this request with ${count} separate requests.`
        : 'Saving updates this request to the dates below.',
      hasOverlap
        ? 'Some added days overlap time off of resources in the same practice area who share a matter.'
        : '',
      check.failed
        ? 'The overlap check was unavailable, so the added days were not checked.'
        : '',
    ]
      .filter(Boolean)
      .join(' ');
    const saveLabel = isSplit ? `Save as ${count} requests` : 'Save';
    const data: ConfirmationDialogData = {
      title: isSplit
        ? `Split into ${count} requests?`
        : 'Save time off changes?',
      message,
      sections: [
        { heading: 'Changes', items: this.describeChanges() },
        {
          heading: isSplit ? 'Resulting requests' : 'New dates',
          items: runs.map((run) => this.formatRun(run)),
        },
        ...(hasOverlap ? [overlapConfirmationSection(check.overlaps)] : []),
      ],
      confirmText: hasOverlap ? `${saveLabel} anyway` : saveLabel,
      cancelText: 'Go Back',
      icon: hasOverlap ? 'warning' : isSplit ? 'call_split' : 'edit_calendar',
      iconColor: hasOverlap
        ? 'var(--dark-orange-warning)'
        : 'var(--lapis-lazuli)',
      highlightColor: hasOverlap
        ? 'var(--state-warning-bg)'
        : 'var(--state-info-bg)',
    };
    this.confirm(data, () => this.submitEdit(result));
  }

  /**
   * Every confirmation this dialog raises is modal in the strict sense: Esc and the backdrop do
   * nothing, so only the two buttons can answer it, and only a yes runs the action.
   */
  private confirm(data: ConfirmationDialogData, onConfirmed: () => void): void {
    this.dialog
      .open(ConfirmationDialogComponent, {
        width: '480px',
        disableClose: true,
        autoFocus: false,
        data,
      })
      .afterClosed()
      .subscribe((confirmed: boolean) => {
        if (confirmed) onConfirmed();
      });
  }

  /**
   * Sends the day set to the API and closes only when it is accepted, so a refusal (an overlap
   * the picker could not see, the day cap, a permission change) leaves the user's edits in place
   * to fix and resend. Which route takes it is the service's call, keyed on ownership.
   */
  private submitEdit(result: TimeOffDetailsDialogResult): void {
    const save$ = this.timeOffService.updateRequestDaysFor(
      this.data.timeOffRequestId,
      result.edit?.days ?? [],
      !!this.data.isOwnRequest,
    );
    const couldClose = this.dialogRef.disableClose;
    this.isSaving = true;
    this.saveError = null;
    // A backdrop click mid-save would drop the result and leave the caller's list stale.
    this.dialogRef.disableClose = true;
    // The date filter now refuses every cell; the calendar only re-reads it on a repaint.
    this.calendar?.updateTodaysDate();
    save$.subscribe({
      next: () => {
        this.isSaving = false;
        this.dialogRef.disableClose = couldClose;
        this.dialogRef.close(result);
      },
      error: (error: unknown) => {
        this.isSaving = false;
        this.dialogRef.disableClose = couldClose;
        // The API's own wording when it gives one (the overlap message names the date).
        this.saveError = describeApiError(
          error,
          'The time off request could not be updated. Try again.',
        );
        this.calendar?.updateTodaysDate();
      },
    });
  }

  /** One line per stretch of removed or added days, e.g. "Removed Mar 4, 2026 - Mar 5, 2026". */
  private describeChanges(): string[] {
    const describe = (days: string[], verb: string) =>
      groupContiguousDays(days, this.holidayKeys).map(
        (run) => `${verb} ${this.formatRange(run)}`,
      );
    return [
      ...describe(this.removedDays, 'Removed'),
      ...describe(this.addedDays, 'Added'),
    ];
  }

  /**
   * Nothing is left to save once every day is removed, so offer the cancellation the user is
   * really after, with the same confirmation and result as the Cancel Time Off button.
   */
  private confirmCancelRequest(): void {
    this.openCancelConfirmation(
      'Every day has been removed, so there is nothing left to save. Cancel the whole request instead, or go back and restore some days.',
    );
  }

  /**
   * The one destructive confirmation behind both ways of cancelling a request, so the footer
   * button and the emptied editor look, read and close the same. Cancelling cannot be undone from
   * this dialog, hence the modal prompt with Go Back as the only way out. A manager note goes out
   * only if the prompt listed it: the note box hides in edit mode, so a draft typed before Edit
   * would otherwise be saved unseen. An owner's pending request takes no note at all, so a typed
   * one is called out as dropped instead.
   */
  private openCancelConfirmation(message: string): void {
    const typed = this.optionalManagerNote;
    const note = this.isOwnPendingRequest ? undefined : typed;
    const dropped = typed && !note;
    this.confirm(
      {
        title: 'Cancel this time off request?',
        message: dropped
          ? `${message} The manager note is not saved when you withdraw your own pending request.`
          : message,
        confirmText: 'Cancel Time Off',
        cancelText: 'Go Back',
        icon: 'event_busy',
        iconColor: 'var(--dark-red-error)',
        highlightColor: 'var(--dark-red-error-bg)',
        confirmColor: 'var(--dark-red-error)',
        ...(note
          ? { sections: [{ heading: 'Manager note', items: [note] }] }
          : {}),
      },
      () => this.dialogRef.close({ action: 'cancel', note }),
    );
  }

  /** "Mar 2, 2026 - Mar 3, 2026 (2 days)": the range plus its business-day count. */
  formatRun(run: DayRun): string {
    return `${this.formatRange(run)} (${run.count} ${run.count === 1 ? 'day' : 'days'})`;
  }

  private formatRange(run: DayRun): string {
    return formatShortDateRange(run.start, run.end);
  }

  private recomputeEditState(): void {
    this.removedDays = [...this.originalDays]
      .filter((key) => !this.selectedDays.has(key))
      .sort();
    this.addedDays = [...this.selectedDays]
      .filter((key) => !this.originalDays.has(key))
      .sort();
    this.resultingRequests = groupContiguousDays(
      this.selectedDays,
      this.holidayKeys,
    );
  }

  /**
   * The request's day list from the caller, or, if a caller did not supply one, every calendar
   * day between the start and end dates as a best effort.
   */
  private resolveOriginalDays(): string[] {
    if (this.data.days?.length) {
      return this.data.days.map(this.normalizeDayKey);
    }
    const start = parseIsoDateKey(this.data.startDate);
    const end = parseIsoDateKey(this.data.endDate) ?? start;
    if (!start || !end) return [];
    const days: string[] = [];
    for (const d = new Date(start); d <= end; d.setDate(d.getDate() + 1)) {
      days.push(toIsoDateKey(d));
    }
    return days;
  }

  /** Day keys arrive as yyyy-MM-dd but tolerate a date-time; only the date part matters. */
  private normalizeDayKey = (value: string): string =>
    value.length > 10 ? value.slice(0, 10) : value;

  /** Drops holiday filler from the caller's list. Re-run once holidays are known. */
  private rebuildOriginalDays(): void {
    this.originalDays = new Set(
      this.rawOriginalDays.filter(
        (key) => !!parseIsoDateKey(key) && !this.holidayKeys.has(key),
      ),
    );
  }

  /**
   * Adding may reach at most MAX_EXTENSION_DAYS beyond either edge of the request, and never
   * past the employee's neighbouring requests: nothing on or before the last day of the previous
   * one, nothing on or after the first day of the next one. The calendar's min and max follow
   * the window so it cannot be scrolled to dates that could never be picked.
   */
  private rebuildAddWindow(): void {
    // Anchor on the stored span, holidays included: a request made up only of holidays shows no
    // days in the picker but still has edges, and its window must not fall open. A request with
    // no dates at all (a caller error) is anchored on today rather than left unbounded.
    const stored = this.rawOriginalDays
      .filter((key) => !!parseIsoDateKey(key))
      .sort();
    const today = toIsoDateKey(new Date());
    const first = stored[0] ?? today;
    const last = stored[stored.length - 1] ?? today;
    const others = [...this.otherRequestKeys].sort();
    const previousRequest = others.filter((key) => key < first).pop();
    const nextRequest = others.find((key) => key > last);
    const limitStart = this.shiftDayKey(first, -(MAX_EXTENSION_DAYS + 1));
    const limitEnd = this.shiftDayKey(last, MAX_EXTENSION_DAYS + 1);
    this.addWindowStart =
      previousRequest && previousRequest > limitStart
        ? previousRequest
        : limitStart;
    this.addWindowEnd =
      nextRequest && nextRequest < limitEnd ? nextRequest : limitEnd;
    this.calendarMinDate = parseIsoDateKey(
      this.shiftDayKey(this.addWindowStart, 1),
    );
    this.calendarMaxDate = parseIsoDateKey(
      this.shiftDayKey(this.addWindowEnd, -1),
    );
  }

  private shiftDayKey(key: string, days: number): string {
    const date = parseIsoDateKey(key) ?? new Date();
    date.setDate(date.getDate() + days);
    return toIsoDateKey(date);
  }

  private isWithinAddWindow(key: string): boolean {
    return (
      (this.addWindowStart === null || key > this.addWindowStart) &&
      (this.addWindowEnd === null || key < this.addWindowEnd)
    );
  }

  private seedHolidaysFromCaller(): void {
    if (this.holidaysLoaded || !this.data.holidays?.length) return;
    this.holidayKeys = holidayDateKeys(this.data.holidays);
    this.holidaysLoaded = true;
  }

  /**
   * Loads holidays once unless the caller supplied them. The external holiday feed is slow, so
   * the picker opens with holiday filler still shown and refines itself when the list lands.
   */
  private ensureHolidays(): void {
    if (this.holidaysLoaded || this.isLoadingHolidays) return;
    this.isLoadingHolidays = true;
    this.holidaysLoadFailed = false;
    this.timeOffService
      .getHolidays()
      .pipe(
        catchError(() => {
          this.holidaysLoadFailed = true;
          return of([] as HolidayDto[]);
        }),
      )
      .subscribe((holidays) => {
        this.isLoadingHolidays = false;
        // Leave the picker as it is and retry on the next edit.
        if (this.holidaysLoadFailed) return;
        this.applyHolidays(holidays);
      });
  }

  /** Filler days that turn out to be holidays leave both day sets; nothing else moves. */
  private applyHolidays(holidays: HolidayDto[]): void {
    this.holidayKeys = holidayDateKeys(holidays);
    this.holidaysLoaded = true;
    if (!this.isEditing) return;
    this.rebuildOriginalDays();
    this.rebuildAddWindow();
    this.selectedDays = new Set(
      [...this.selectedDays].filter(
        (key) => this.originalDays.has(key) || !this.holidayKeys.has(key),
      ),
    );
    this.recomputeEditState();
    this.calendar?.updateTodaysDate();
  }

  private formatUserName(updatedBy: {
    firstName?: string;
    lastName?: string;
  }): string {
    return [updatedBy.firstName, updatedBy.lastName]
      .filter((part) => !!part && !!part.trim())
      .join(' ')
      .trim();
  }

  private getSlotBadgeClass(slot: string): string {
    switch (slot) {
      case 'APPROVED':
        return 'badge-approved';
      case 'DENIED':
        return 'badge-denied';
      default:
        return '';
    }
  }

  private getSlotStatusLabel(slot: string): string {
    switch (slot) {
      case 'APPROVED':
        return 'Approved';
      case 'DENIED':
        return 'Returned';
      default:
        return '';
    }
  }

  private formatTimestamp(value?: string): string {
    if (!value) return '';
    const d = new Date(value);
    if (isNaN(d.getTime())) return '';
    return d.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
    });
  }

  onNoteInputChange(): void {
    if (this.noteError && this.managerNoteInput.trim()) {
      this.noteError = false;
    }
  }

  /**
   * The header X. Ignored mid-save: disableClose only covers the backdrop and Escape, and closing
   * here would drop the in-flight result so the caller never refreshes.
   */
  close(): void {
    if (this.isEditBusy) return;
    this.dialogRef.close(null);
  }

  approve(): void {
    this.dialogRef.close({
      action: 'approve',
      note: this.optionalManagerNote,
    });
  }

  return(): void {
    const note = this.managerNoteInput.trim();
    if (!note) {
      this.noteError = true;
      return;
    }
    this.dialogRef.close({ action: 'return', note });
  }

  /** The footer button: asks first, naming the request, then closes for the host to cancel it. */
  cancelRequest(): void {
    const range = this.dateRangeLabel;
    this.openCancelConfirmation(
      range
        ? `${this.data.typeName || 'This time off'} for ${range} will be cancelled.`
        : 'This time off request will be cancelled.',
    );
  }
}
